import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { PrinterTarget, PrinterTargetRole, User } from 'src/typings';
import { NotificationService } from '../services/notification.service';
import { CurrentUserService as UserService } from '../user/user.service';
import { ComputerService } from './computer.service';
import {
  ThermalPrintTargetDialogComponent,
  ThermalPrintTargetDialogResult
} from './thermal-print-target-dialog.component';

type ThermalTargetOption = PrinterTarget & {
  targetId: string;
  label: string;
  role: PrinterTargetRole;
  status: 'online' | 'offline';
};

type ChooseTargetOptions = {
  role: PrinterTargetRole;
  currentTargetId?: string | null;
  warehouseId?: number | null;
  profileId?: number | string | null;
  user?: User | undefined;
  title?: string;
  description?: string;
  makeDefaultLabel?: string;
  fieldLabel?: string;
  submitLabel?: string;
};

@Injectable({
  providedIn: 'root'
})
export class ThermalPrintTargetService {
  constructor(
    private readonly computerService: ComputerService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly userService: UserService
  ) {}

  getDefaultAgentName(user: User | undefined): string | null {
    return user?.defaultThermalPrinter?.trim() || null;
  }

  async chooseAgent(
    user: User | undefined,
    labels?: Pick<
      ChooseTargetOptions,
      'title' | 'description' | 'makeDefaultLabel' | 'fieldLabel' | 'submitLabel'
    >
  ): Promise<string | null> {
    const selection = await this.chooseTarget({
      role: 'receipt',
      user,
      currentTargetId: user?.defaultThermalPrinter?.trim() || undefined,
      ...labels
    });

    return selection?.agentName || selection?.targetId || null;
  }

  async chooseTerminalAgent(user: User | undefined): Promise<string | null> {
    return this.chooseAgent(user, {
      title: 'Выберите агент терминала',
      description: 'Выберите онлайн-агент, который отправит оплату на POS-терминал.',
      makeDefaultLabel: 'Использовать этот агент по умолчанию',
      fieldLabel: 'Агент',
      submitLabel: 'Выбрать агент'
    });
  }

  async chooseTarget(options: ChooseTargetOptions): Promise<ThermalTargetOption | null> {
    const currentTargetId =
      options.currentTargetId ||
      (options.role === 'receipt' ? this.getCurrentTargetId(options.user, options.role) : undefined);
    const targets = await this.getSelectableTargets(
      options.role,
      currentTargetId || undefined,
      options.warehouseId,
      options.profileId
    );

    if (!targets.length) {
      this.notificationService.warning('Нет доступных онлайн-агентов для этого действия.');
      return null;
    }

    const result = await firstValueFrom(
      this.dialog
        .open(ThermalPrintTargetDialogComponent, {
          width: '460px',
          maxWidth: '95vw',
          autoFocus: false,
          data: {
            currentTargetId: currentTargetId || targets[0].targetId,
            options: targets,
            role: options.role,
            title:
              options.title ||
              (options.role === 'label' ? 'Выберите принтер этикеток' : 'Выберите принтер чеков'),
            description: options.description || (
              options.role === 'label'
                ? 'Выберите онлайн-принтер этикеток для этой печати.'
                : 'Выберите онлайн-принтер чеков для этой печати.'
            ),
            makeDefaultLabel: options.makeDefaultLabel || (
              options.role === 'label'
                ? 'Использовать этот профиль этикеток по умолчанию'
                : 'Использовать этот принтер чеков по умолчанию'
            ),
            fieldLabel: options.fieldLabel,
            submitLabel: options.submitLabel
          }
        })
        .afterClosed()
    );

    const selection = result as ThermalPrintTargetDialogResult | undefined;
    if (!selection?.targetId) {
      return null;
    }

    const selectedTarget = targets.find((target) => target.targetId === selection.targetId);
    if (!selectedTarget) {
      return null;
    }

    if (selection.makeDefault && options.user?.id) {
      if (options.role === 'receipt') {
        if (selectedTarget.agentName && selectedTarget.agentName !== options.user.defaultThermalPrinter) {
          this.userService.update({
            id: options.user.id,
            thermalPrinter: selectedTarget.agentName
          });
        }
      } else if (
        selectedTarget.profileId !== undefined ||
        selectedTarget.profileCode ||
        selectedTarget.code
      ) {
        this.userService.update({
          id: options.user.id,
          defaultLabelProfileId: selectedTarget.profileId ?? selectedTarget.id,
          defaultLabelProfileCode: selectedTarget.profileCode ?? selectedTarget.code ?? null
        });
      }
    }

    return selectedTarget;
  }

  async getSelectableAgents(currentAgentName?: string) {
    return this.getSelectableTargets('receipt', currentAgentName?.trim() || undefined);
  }

  async getSelectableTargets(
    role: PrinterTargetRole,
    currentTargetId?: string,
    warehouseId?: number | null,
    profileId?: number | string | null
  ) {
    const normalizedTargets = await this.loadTargets(role, warehouseId, profileId);
    const sorted = [...normalizedTargets].sort((left, right) => {
      if (left.targetId === currentTargetId) return -1;
      if (right.targetId === currentTargetId) return 1;
      if (left.status !== right.status) {
        return left.status === 'online' ? -1 : 1;
      }
      return left.label.localeCompare(right.label);
    });

    const currentTarget = sorted.find((target) => target.targetId === currentTargetId);
    if (currentTargetId && !currentTarget) {
      sorted.unshift({
        id: currentTargetId,
        targetId: currentTargetId,
        role,
        status: 'offline',
        label: `${currentTargetId} (не в сети, сохраненный выбор)`
      });
    }

    return sorted;
  }

  private async loadTargets(
    role: PrinterTargetRole,
    warehouseId?: number | null,
    profileId?: number | string | null
  ) {
    try {
      const targets = await firstValueFrom(
        this.computerService.getPrinterTargets(role, warehouseId, profileId)
      );
      return this.normalizeTargets(targets, role);
    } catch (error) {
      if (role === 'receipt') {
        try {
          const agents = await firstValueFrom(this.computerService.getConnectedWorkstationAgents());
          return this.normalizeConnectedAgents(agents);
        } catch (fallbackError) {
          console.error(fallbackError || error);
        }
      } else {
        console.error(error);
      }
      return [];
    }
  }

  private normalizeTargets(targets: PrinterTarget[], role: PrinterTargetRole): ThermalTargetOption[] {
    return targets.map((target) => {
      const profileId = target.profileId ?? target.profile?.profileId ?? target.profile?.id ?? null;
      const profileCode =
        target.profileCode ?? target.profile?.profileCode ?? target.profile?.code ?? null;
      const profileName =
        target.profileName ?? target.profile?.profileName ?? target.profile?.name ?? null;
      const targetId = this.getTargetId(target, role);

      return {
        ...target,
        id: target.id ?? targetId,
        profileId,
        profileCode,
        profileName,
        targetId,
        role: target.role || role,
        status: target.status || (target.isConnected === false ? 'offline' : 'online'),
        label: this.buildTargetLabel(target, role, targetId)
      };
    });
  }

  private normalizeConnectedAgents(
    agents: Array<{
      agentName: string;
      machineName: string;
      userName: string;
      agentVersion?: string | null;
    }>
  ): ThermalTargetOption[] {
    return agents.map((agent) => ({
      id: agent.agentName,
      targetId: agent.agentName,
      agentName: agent.agentName,
      agentVersion: agent.agentVersion,
      machineName: agent.machineName,
      userName: agent.userName,
      role: 'receipt',
      status: 'online',
      label: this.appendAgentVersion(
        `${agent.agentName} (${agent.machineName}/${agent.userName})`,
        agent.agentVersion
      )
    }));
  }

  private buildTargetLabel(
    target: PrinterTarget,
    role: PrinterTargetRole,
    targetId: string
  ): string {
    if (target.label) return target.label;

    if (role === 'receipt') {
      const version = target.agentVersion;
      if (target.displayName) {
        const label = target.agentName && target.displayName !== target.agentName
          ? `${target.displayName} (${target.agentName})`
          : target.displayName;
        return this.appendAgentVersion(label, version);
      }
      if (target.agentName) {
        return this.appendAgentVersion(target.agentName, version);
      }
      return this.appendAgentVersion(targetId, version);
    }

    const profileLabel =
      target.profileName ||
      target.profile?.name ||
      target.profileCode ||
      target.profile?.code ||
      target.code ||
      'Этикетка';
    const printerLabel = target.displayName || target.agentName || target.deviceId || targetId;

    if (printerLabel) {
      return this.appendAgentVersion(`${profileLabel} - ${printerLabel}`, target.agentVersion);
    }

    return profileLabel;
  }

  private appendAgentVersion(label: string, version?: string | null) {
    const formattedVersion = this.formatAgentVersion(version);
    return formattedVersion ? `${label} v${formattedVersion}` : label;
  }

  private formatAgentVersion(version?: string | null) {
    const trimmed = String(version || '').trim();
    if (!trimmed) {
      return '';
    }

    const [main, metadata] = trimmed.split('+', 2);
    if (!metadata) {
      return main;
    }

    return `${main}+${metadata.split('.', 1)[0]}`;
  }

  private getTargetId(target: PrinterTarget, role: PrinterTargetRole): string {
    if (role === 'label') {
      if (target.deviceId) return target.deviceId;
      if (target.agentName) return target.agentName;
      if (target.profileCode) return target.profileCode;
    }

    if (target.agentName) return target.agentName;
    if (target.deviceId) return target.deviceId;
    if (target.profileCode) return target.profileCode;
    if (target.profileId !== undefined && target.profileId !== null) return String(target.profileId);
    if (target.code) return target.code;

    return String(target.id);
  }

  private getCurrentTargetId(user: User | undefined, role: PrinterTargetRole): string | undefined {
    if (!user) return undefined;

    if (role === 'receipt') {
      return user.defaultThermalPrinter?.trim() || undefined;
    }

    return (
      user.defaultLabelProfile?.profileCode?.trim() ||
      user.defaultLabelProfile?.code?.trim() ||
      (user.defaultLabelProfile?.profileId !== undefined && user.defaultLabelProfile?.profileId !== null
        ? String(user.defaultLabelProfile.profileId)
        : undefined) ||
      (user.defaultLabelProfile?.id !== undefined && user.defaultLabelProfile?.id !== null
        ? String(user.defaultLabelProfile.id)
        : undefined)
    );
  }
}
